/**
 * =============================================================================
 * IDS CRM Web - Settings → My Menu (2026-08-10)
 * =============================================================================
 *
 * The sidebar customisation checkboxes, moved out of the Layout modal into
 * Settings where personal preferences live. Same registry as the sidebar
 * (utils/navItems.js), same endpoint (PUT /auth/me/ui-preferences), same
 * guarantees: cosmetic and desktop-only — routes stay open and the mobile
 * More sheet always lists everything the role can reach.
 *
 * =============================================================================
 */

import React, { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../components/Toast';
import { getCapabilities } from '../utils/permissions';
import { navCustomiseItems } from '../utils/navItems';

function MenuSettings() {
  const { authFetch, user, updateUser } = useAuth();
  const { toastElement, showToast } = useToast();

  const items = navCustomiseItems(getCapabilities(user)).filter(item => item.show);
  const [hiddenSet, setHiddenSet] = useState(() => new Set(user?.ui_preferences?.hidden_nav || []));
  const [saving, setSaving] = useState(false);

  const toggle = (key) => {
    setHiddenSet(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  };

  const save = async () => {
    setSaving(true);
    try {
      const response = await authFetch('/api/v1/auth/me/ui-preferences', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ hidden_nav: [...hiddenSet] })
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data.message || 'Failed to save');
      updateUser({ ui_preferences: data.ui_preferences });
      showToast('Menu preferences saved', 'success');
    } catch (err) {
      showToast(err.message, 'error');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="admin-section">
      {toastElement}
      <div className="section-header">
        <div>
          <h2>My Menu</h2>
          <p className="section-hint">
            Untick anything you don't use — it disappears from your sidebar only.
            Every page stays available under More on mobile, and you can bring
            items back here any time.
          </p>
        </div>
      </div>

      <div className="menu-settings-list">
        {items.map(item => (
          <label key={item.key} className="menu-customise-row">
            <input
              type="checkbox"
              checked={!hiddenSet.has(item.key)}
              onChange={() => toggle(item.key)}
            />
            {item.label}
          </label>
        ))}
      </div>

      <div className="menu-settings-actions">
        <button type="button" className="btn-primary" disabled={saving} onClick={save}>
          {saving ? 'Saving…' : 'Save'}
        </button>
      </div>
    </div>
  );
}

export default MenuSettings;
